import { Component, createRef } from "react"

/* Components */
import { DataLayout, Icon, Avatar } from "../../components"

/* Widgets */
import { CategoryModal, GroupsModal } from "../../widgets/category"
import { toast } from "../../widgets/common"

/* Shared */
import { pictureOf, plural, toneOf } from "../../widgets/category/shared"

/* Helpers */
import { withRouter } from "../../helpers"

/* REST API */
import { list, status } from "../../api/Category"


/* Page Categories — every category of the platform at once, laid out by the groups of the home
   screen of the app. There are a few dozen of them, so the page reads them in one call and the
   tabs and the search work right on the page */
class Categories extends Component {

    constructor() {
        super()

        this.state = {
            status: "loading",
            data: [],
            groups: [],

            /* "all" — every group one after another, or the id of one group */
            tab: "all",
            search: ""
        }

        /* The card of a category and the groups are opened by the reference */
        this._category = createRef()
        this._groups = createRef()
    }

    componentDidMount = () => {

        window.scrollTo(0, 0)
        this.load(true)
    }

    /* Load action */
    load = (reload = false) => {

        /* Reload */
        if (reload) {
            this.setState({ status: "loading" })
        }

        list({}).then(response => {
            if (response && response.status === 200) {

                const { tab } = this.state
                const groups = response.data.groups

                /* The tab of a group which is gone falls back to all of them */
                const exists = tab === "all" || groups.some(group => group.id === tab)

                this.setState({ status: "ok", data: response.data.list, groups, tab: exists ? tab : "all" })
            }
            else {
                this.setState({ status: "error", data: [], groups: [] })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [], groups: [] })
        })
    }

    /* The groups were changed in their window — the tabs follow at once, the cards are read again
       because a renamed group renames the line of each of its categories */
    groupsChanged = (groups, reload = true) => {
        this.setState({ groups }, () => {
            if (reload) {
                this.load()
            }
        })
    }

    /* Show the category in the app or hide it — right from the card */
    change = (event, item) => {

        event.stopPropagation()

        const value = parseInt(item.visible) === 1 ? 0 : 1

        status({ id: item.id, value }).then(response => {

            if (response && response.status === 200) {

                const data = this.state.data.map(element => element.id === item.id ? { ...element, visible: value } : element)
                this.setState({ data })

                toast.success(value === 1 ? "Категория показана в приложении" : "Категория скрыта")
            }
            else {
                toast.error("Что-то пошло не так! Попробуйте позднее")
            }
        }).catch(() => {
            toast.error("Что-то пошло не так! Попробуйте позднее")
        })
    }

    /* The categories which the search lets through */
    filtered = () => {

        /* Fields */
        const { data, search } = this.state

        const query = search.trim().toLowerCase()

        if (query.length === 0) {
            return data
        }

        return data.filter(item => `${item.name}`.toLowerCase().includes(query) || `${item.typeName}`.toLowerCase().includes(query))
    }

    /* Draw Header */
    _header = () => {

        /* Fields */
        const { search } = this.state

        return (
            <div className="organization-header">

                <div className="page-header-text">Категории</div>

                <div className="organization-header-tools">

                    <form onSubmit={event => event.preventDefault()} className="organization-search">

                        <div className="organization-search-icon">
                            <Icon name="search" size={17} />
                        </div>

                        <input placeholder="Категория или группа" value={search} onChange={event => this.setState({ search: event.target.value })} />

                        {search.length > 0 &&
                            <div onClick={() => this.setState({ search: "" })} className="organization-search-clear">
                                <Icon name="close" size={11} width={2.4} />
                            </div>
                        }

                    </form>

                    <div onClick={() => this._groups.current.open()} className="category-groups-button">
                        <Icon name="layers" size={18} />
                        <span>Группы</span>
                    </div>

                    <div onClick={() => this._category.current.open(null, this.state.tab === "all" ? 0 : this.state.tab)} className="organization-create">
                        <Icon name="plus" size={17} width={2.4} />
                        <span>Создать</span>
                    </div>

                </div>

            </div>
        )
    }

    /* The numbers over the cards */
    _summary = () => {

        /* Fields */
        const { data, groups } = this.state

        const shown = data.filter(item => parseInt(item.visible) === 1).length
        const hidden = data.length - shown
        const empty = data.filter(item => (parseInt(item.organizations) || 0) === 0).length

        const cards = [
            { icon: "category", value: data.length, text: plural(data.length, "категория", "категории", "категорий"), accent: "blue" },
            { icon: "layers", value: groups.length, text: plural(groups.length, "группа", "группы", "групп"), accent: "violet" },
            { icon: "eye", value: shown, text: "в приложении", accent: "green" },
            { icon: "eyeOff", value: hidden, text: "скрыто", accent: "grey" },
            { icon: "organization", value: empty, text: "без точек", accent: "orange" }
        ]

        return (
            <div className="category-summary">
                {cards.map(card =>
                    <div className={`category-summary-item ${card.accent}`} key={card.icon}>
                        <div className="category-summary-icon">
                            <Icon name={card.icon} size={18} width={2} />
                        </div>
                        <div className="category-summary-text">
                            <b>{card.value}</b>
                            <span>{card.text}</span>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    /* The tabs of the groups */
    _tabs = () => {

        /* Fields */
        const { tab, groups } = this.state

        const items = this.filtered()

        return (
            <div className="category-tabs">

                <div onClick={() => this.setState({ tab: "all" })} className={`category-tab ${tab === "all" ? "active" : ""}`}>
                    Все
                    <span>{items.length}</span>
                </div>

                {groups.map(group => {

                    const tone = toneOf(group.id)
                    const count = items.filter(item => parseInt(item.typeID) === group.id).length

                    return (
                        <div
                            key={`${group.id}`}
                            onClick={() => this.setState({ tab: group.id })}
                            style={{ "--tone": tone.accent, "--tone-soft": tone.soft }}
                            className={`category-tab toned ${tab === group.id ? "active" : ""} ${parseInt(group.visible) === 1 ? "" : "hidden"}`}
                        >
                            <i />
                            {group.name}
                            {parseInt(group.visible) !== 1 && <Icon name="eyeOff" size={13} width={2} />}
                            <span>{count}</span>
                        </div>
                    )
                })}

            </div>
        )
    }

    /* One card of a category */
    _card = item => {

        const shown = parseInt(item.visible) === 1
        const organizations = parseInt(item.organizations) || 0
        const tone = toneOf(item.typeID)

        return (
            <div
                key={`${item.id}`}
                onClick={() => this._category.current.open(item)}
                style={{ "--tone": tone.accent, "--tone-soft": tone.soft }}
                className={`category-card ${shown ? "" : "off"} ${item.size === "big" ? "big" : ""}`}
            >

                <div className="category-card-cover">

                    <Avatar className="category-card-picture" src={pictureOf(item.image)} alt={item.name}>
                        <Icon name="category" size={28} />
                    </Avatar>

                    {item.webImage &&
                        <div className="category-card-web" title="Иконка сайта">
                            <img src={pictureOf(item.webImage)} alt="Web" />
                        </div>
                    }

                    {item.size === "big" && <div className="category-card-badge">Широкая</div>}

                    <div onClick={event => this.change(event, item)} className={`category-card-toggle ${shown ? "on" : ""}`} title={shown ? "Скрыть в приложении" : "Показать в приложении"}>
                        <i />
                    </div>

                </div>

                <div className="category-card-body">
                    <div className="category-card-name">{item.name}</div>
                    <div className="category-card-meta">
                        <span className={organizations === 0 ? "empty" : ""}>
                            <Icon name="organization" size={13} width={2} />
                            {organizations === 0 ? "Нет точек" : `${organizations} ${plural(organizations, "точка", "точки", "точек")}`}
                        </span>
                        <em className={shown ? "on" : "off"}>{shown ? "Видна" : "Скрыта"}</em>
                    </div>
                </div>

            </div>
        )
    }

    /* The empty place at the end of a group — a new category straight into it */
    _add = group => {
        return (
            <div key="add" onClick={() => this._category.current.open(null, group.id)} className="category-card add">
                <div className="category-card-add-icon">
                    <Icon name="plus" size={20} width={2.2} />
                </div>
                <span>Добавить в «{group.name}»</span>
            </div>
        )
    }

    /* One group — its title and its cards */
    _section = (group, items) => {

        const tone = toneOf(group.id)
        const shown = parseInt(group.visible) === 1

        return (
            <div className="category-section" key={`${group.id}`} style={{ "--tone": tone.accent, "--tone-soft": tone.soft }}>

                <div className="category-section-head">
                    <div className="category-section-mark" />
                    <div className="category-section-title">{group.name}</div>
                    <div className="category-section-count">{items.length}</div>
                    {!shown && <div className="category-section-hidden"><Icon name="eyeOff" size={13} width={2} />Скрыта в приложении</div>}
                </div>

                <div className="category-grid">
                    {items.map(item => this._card(item))}
                    {this.state.search.trim().length === 0 && this._add(group)}
                </div>

            </div>
        )
    }

    /* Draw Content */
    _content = () => {

        /* Fields */
        const { status, data, groups, tab, search } = this.state

        if (status !== "ok") {
            return (
                <div className="page-content">
                    <DataLayout status={status} reload={() => this.load(true)} data={data} />
                </div>
            )
        }

        const items = this.filtered()
        const visibleGroups = tab === "all" ? groups : groups.filter(group => group.id === tab)

        /* While searching only the groups with a match are left */
        const sections = visibleGroups
            .map(group => ({ group, items: items.filter(item => parseInt(item.typeID) === group.id) }))
            .filter(section => search.trim().length === 0 || section.items.length > 0)

        /* A category whose group is gone still has to be found and moved */
        const orphans = tab === "all" ? items.filter(item => !groups.some(group => group.id === parseInt(item.typeID))) : []

        return (
            <div className="page-content">

                {this._summary()}

                {this._tabs()}

                {sections.length === 0 && orphans.length === 0 &&
                    <DataLayout status="ok" data={[]} empty={{ text: "Попробуйте ещё раз, изменив поиск или группу" }} onReset={() => this.setState({ search: "", tab: "all" })} />
                }

                {sections.map(section => this._section(section.group, section.items))}

                {orphans.length > 0 &&
                    <div className="category-section orphans">
                        <div className="category-section-head">
                            <div className="category-section-mark" />
                            <div className="category-section-title">Без группы</div>
                            <div className="category-section-count">{orphans.length}</div>
                            <div className="category-section-hidden"><Icon name="alert" size={13} width={2} />Не видны в приложении — выберите им группу</div>
                        </div>
                        <div className="category-grid">
                            {orphans.map(item => this._card(item))}
                        </div>
                    </div>
                }

            </div>
        )
    }

    render = () => {

        return (
            <div className="page">

                {/* Draw Header */}
                {this._header()}

                {/* Draw Content */}
                {this._content()}

                {/* Draw Category */}
                <CategoryModal ref={this._category} groups={this.state.groups} onSaved={() => this.load()} />

                {/* Draw Groups */}
                <GroupsModal ref={this._groups} groups={this.state.groups} onChanged={this.groupsChanged} />

            </div>
        )
    }
}

export default withRouter(Categories)
